// Agent ticket management: full detail, public replies + internal notes, status
// / label / priority / assignment / escalation controls, GitHub issue linking
// and the audit trail.
import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import Select from "react-select";
import { brand as themeBrand } from "../../../config/theme";
import { useTheme } from "../../../context/ThemeContext";
import { useUserStore } from "../../../store/userStore";
import { authenticateUser } from "../../../pages/login/functions";
import { useAlert } from "../../../context/AlertContext";
import { useIsReseller } from "../../../hooks/useIsReseller";
import { useCompaniesStore } from "../../../store/admin/companiesStore";
import {
  getTicket, updateTicket, deleteTicket, addAgentComment, linkGithubIssue, linkExistingGithubIssue,
  getSettings, getAssignableUsers, getCompanyUsers, getGithubRepositories, formatDate, allowedNextStatuses,
  releaseAiAgent, approveAiAgent, revokeAiAgentApproval, tsToDate, setTicketStage, retryServiceDeskBridge,
  type Ticket, type DevStageId, type Comment, type AuditEntry, type ServiceDeskSettings,
  type AssignableUser, type AffectedUser, type TicketPriority, type GithubRepository,
} from "../services/serviceDeskService";
import { buildAffectedUserOptions } from "../affectedUserOptions";
import { SlaBadge, PriorityBadge, AiAgentBadge, ReworkChip } from "../components/Badges";
import { canCloseTicket, formatClosureDate, formatDurationMs, CLOSED_STATUS } from "../services/ticketClosure";
import { AiAgentActions } from "../components/AiAgentActions";
import { DevStagePanel } from "../components/DevStagePanel";
import { EngineeringBridgeStatus } from "../components/EngineeringBridgeStatus";
import { bridgeRepoChoices, engineeringBridgeNeedsRepo } from "../services/engineeringBridge";
import { apiErrorMessage } from "../services/agentsPanel";
import { AiReplyPanel } from "../components/AiReplyPanel";
import { CommentThread } from "../components/CommentThread";
import { AgentSelector } from "../components/AgentSelector";
import { SERVICE_DESK_BASE } from "../config";
import { MODULE_OPTIONS } from "../moduleOptions";
import { legacyTicketLabels } from "../labelUtils";
import { escapeHtml } from "../../../utils/escapeHtml";

export default function TicketDetailSection() {
  const { t } = useTranslation("serviceDesk");
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { user } = useUserStore();
  const { showAlert } = useAlert();
  const { isReseller } = useIsReseller();
  const { allCompanies, fetchAllCompanies, fetchCompany } = useCompaniesStore();
  // Nombre de la compañía del ticket para quien no es reseller: /companyList y
  // /companyListAll ya sólo le devuelven la suya, así que se pide por id (como
  // IntegradoresMonitor). GET /company sólo responde la propia; si es otra, se
  // queda el id.
  const [resolvedCompanyNames, setResolvedCompanyNames] = useState<Record<string, string>>({});
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [settings, setSettings] = useState<ServiceDeskSettings | null>(null);
  const [users, setUsers] = useState<AssignableUser[]>([]);
  const [companyUsers, setCompanyUsers] = useState<AffectedUser[]>([]);
  const [companyUsersLoading, setCompanyUsersLoading] = useState(false);
  const [repositories, setRepositories] = useState<GithubRepository[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [showGithubModal, setShowGithubModal] = useState(false);
  const [selectedRepo, setSelectedRepo] = useState<{ value: string; label: string } | null>(null);

  // Carga de solo lectura: no mutar el ticket al abrirlo. La compañía se
  // asigna explícitamente desde el selector de gestión (patch), nunca en load.
  const load = useCallback(async () => {
    const data = await getTicket(id);
    if (!data) return;
    setTicket(data.ticket ?? null);
    setComments(data.comments || []);
    setAudit(data.audit || []);
  }, [id]);

  const showAuthModal = useCallback(async () => {
    const { value: formValues } = await Swal.fire({
      title: "Verificar cuenta",
      html: `
        <input id="swal-username" class="swal2-input" placeholder="Usuario o correo electrónico">
        <input id="swal-password" class="swal2-input" type="password" placeholder="Contraseña">
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: "Verificar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: themeBrand.primary,
      background: isDarkMode ? "#1f2937" : "#fff",
      color: isDarkMode ? "#fff" : "#1f2937",
      preConfirm: () => {
        const username = (document.getElementById('swal-username') as HTMLInputElement)?.value;
        const password = (document.getElementById('swal-password') as HTMLInputElement)?.value;
        if (!username || !password) {
          Swal.showValidationMessage('Por favor ingresa usuario y contraseña');
          return false;
        }
        return { username, password };
      }
    });

    if (formValues) {
      await authenticateUser(
        formValues.username,
        formValues.password,
        navigate,
        showAlert,
        t
      );
      // authenticateUser maneja sus errores internamente y no lanza excepciones:
      // verificar en el store si la sesión realmente se creó.
      if (useUserStore.getState().user) {
        // Si la autenticación fue exitosa, recargar el componente
        window.location.reload();
      } else {
        await Swal.fire({
          icon: "error",
          title: "Error de autenticación",
          text: "Usuario o contraseña incorrectos",
          confirmButtonColor: themeBrand.primary,
          background: isDarkMode ? "#1f2937" : "#fff",
          color: isDarkMode ? "#fff" : "#1f2937",
        });
        navigate(`${SERVICE_DESK_BASE}/tickets`);
      }
    } else {
      navigate(`${SERVICE_DESK_BASE}/tickets`);
    }
  }, [navigate, showAlert, t, isDarkMode, themeBrand.primary]);

  useEffect(() => {
    // Verificar autenticación antes de cargar el ticket
    if (!user) {
      setAuthChecked(true);
      showAuthModal();
      return;
    }

    setAuthChecked(true);
    // Cargar compañías si es reseller
    if (isReseller) {
      fetchAllCompanies();
    }
    Promise.all([load(), getSettings().then(setSettings), getAssignableUsers().then(setUsers), getGithubRepositories().then(setRepositories)])
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id, load, user, showAuthModal, isReseller, fetchAllCompanies]);

  useEffect(() => {
    const companyId = ticket?.companyId;
    if (isReseller || !companyId) return;
    if (allCompanies.some((c) => c.id === companyId) || resolvedCompanyNames[companyId]) return;
    let cancelled = false;
    fetchCompany(companyId)
      .then((company) => {
        const name = company?.name || company?.business_name;
        if (!cancelled && name) {
          setResolvedCompanyNames((prev) => ({ ...prev, [companyId]: name }));
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [ticket?.companyId, isReseller, allCompanies, fetchCompany, resolvedCompanyNames]);

  // Usuarios de la compañía del ticket, para poder corregir el usuario afectado
  // desde la ficha. Se recarga cuando cambia la compañía asignada.
  useEffect(() => {
    const companyId = ticket?.companyId;
    setCompanyUsers([]);
    if (!companyId) return;
    let cancelled = false;
    setCompanyUsersLoading(true);
    getCompanyUsers(companyId)
      .then((list) => {
        if (!cancelled) setCompanyUsers(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setCompanyUsers([]);
      })
      .finally(() => {
        if (!cancelled) setCompanyUsersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [ticket?.companyId]);

  const patch = async (p: Parameters<typeof updateTicket>[1]) => {
    setSaving(true);
    try {
      const updated = await updateTicket(id, p);
      setTicket(updated);
      await load();
    } catch (err: unknown) {
      // p. ej. 403: sin permiso para re-atribuir la compañía, o etiquetas/
      // categoría/prioridad que sólo cambia el staff con permiso de Mesa de
      // Servicio vigente; 422: usuario de otra compañía o etiquetas inválidas.
      const data = (err as { response?: { data?: { meta?: { message?: string }; message?: string } } })?.response?.data;
      Swal.fire({
        icon: "error",
        title: t("ticketDetail.updateError"),
        text: typeof data?.message === "string" ? data.message : typeof data?.meta?.message === "string" ? data.meta.message : "",
        background: isDarkMode ? "#1f2937" : "#fff",
        color: isDarkMode ? "#fff" : "#1f2937",
      });
    } finally {
      setSaving(false);
    }
  };

  const onReply = async (body: string, files: File[], internal: boolean) => {
    await addAgentComment(id, body, internal, files);
    await load();
  };

  const onLinkGithub = async () => {
    if (repositories.length === 0) {
      Swal.fire({
        icon: "error",
        title: "Sin repositorios",
        text: "No hay repositorios de GitHub habilitados para esta mesa. Configura GH_DEFAULT_REPO (o los repositorios de la mesa) y GH_TOKEN en el servidor.",
        confirmButtonColor: themeBrand.primary,
        background: isDarkMode ? "#1f2937" : "#fff",
        color: isDarkMode ? "#fff" : "#1f2937",
      });
      return;
    }
    setShowGithubModal(true);
    setSelectedRepo(null);
  };

  const handleGithubConfirm = async () => {
    if (!selectedRepo) return;
    setShowGithubModal(false);
    try {
      const updated = await linkGithubIssue(id, { repo: selectedRepo.value });
      setTicket(updated);
      await load();
      Swal.fire({ icon: "success", title: t("ticketDetail.github.success"), confirmButtonColor: themeBrand.primary, background: isDarkMode ? "#1f2937" : "#fff", color: isDarkMode ? "#fff" : "#1f2937" });
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { meta?: { message?: string } } } })?.response?.data?.meta?.message;
      Swal.fire({ icon: "error", title: t("ticketDetail.github.error"), text: typeof msg === "string" ? msg : "", background: isDarkMode ? "#1f2937" : "#fff", color: isDarkMode ? "#fff" : "#1f2937" });
    }
  };

  const onLinkExistingGithub = async () => {
    const { value: url } = await Swal.fire({
      title: t("ticketDetail.github.linkModalTitle"),
      input: "url",
      inputLabel: t("ticketDetail.github.linkModalLabel"),
      inputPlaceholder: t("ticketDetail.github.linkModalPlaceholder"),
      showCancelButton: true,
      confirmButtonText: t("ticketDetail.github.linkModalConfirm"),
      confirmButtonColor: themeBrand.primary,
      inputValidator: (v) => (!v ? t("ticketDetail.github.linkModalValidator") : undefined),
      background: isDarkMode ? "#1f2937" : "#fff",
      color: isDarkMode ? "#fff" : "#1f2937",
    });
    if (!url) return;
    try {
      const updated = await linkExistingGithubIssue(id, url);
      setTicket(updated);
      await load();
      Swal.fire({ icon: "success", title: t("ticketDetail.github.linkSuccess"), confirmButtonColor: themeBrand.primary, background: isDarkMode ? "#1f2937" : "#fff", color: isDarkMode ? "#fff" : "#1f2937" });
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { meta?: { message?: string } } } })?.response?.data?.meta?.message;
      Swal.fire({ icon: "error", title: t("ticketDetail.github.linkError"), text: typeof msg === "string" ? msg : "", background: isDarkMode ? "#1f2937" : "#fff", color: isDarkMode ? "#fff" : "#1f2937" });
    }
  };

  const onDeleteTicket = async () => {
    const { isConfirmed } = await Swal.fire({
      title: "¿Eliminar ticket?",
      html: `Se eliminará permanentemente el ticket <strong>${escapeHtml(String(ticket?.reference ?? ""))}</strong>. Esta acción no se puede deshacer.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Eliminar",
      confirmButtonColor: "#dc2626",
      cancelButtonText: "Cancelar",
      background: isDarkMode ? "#1f2937" : "#ffffff",
      color: isDarkMode ? "#ffffff" : "#1f2937",
      iconColor: isDarkMode ? "#fbbf24" : "#f59e0b",
      didOpen: () => {
        const title = document.querySelector<HTMLElement>(".swal2-title");
        const content = document.querySelector<HTMLElement>(".swal2-html-container");
        if (title) {
          title.style.color = isDarkMode ? "#ffffff" : "#1f2937";
        }
        if (content) {
          content.style.color = isDarkMode ? "#e5e7eb" : "#374151";
        }
      },
    });

    if (!isConfirmed) return;

    try {
      await deleteTicket(id);
      await Swal.fire({
        icon: "success",
        title: "Ticket eliminado",
        text: "El ticket ha sido eliminado correctamente",
        confirmButtonColor: themeBrand.primary,
        background: isDarkMode ? "#1f2937" : "#fff",
        color: isDarkMode ? "#fff" : "#1f2937",
        didOpen: () => {
          const title = document.querySelector<HTMLElement>(".swal2-title");
          const content = document.querySelector<HTMLElement>(".swal2-html-container");
          if (title) {
            title.style.color = isDarkMode ? "#ffffff" : "#1f2937";
          }
          if (content) {
            content.style.color = isDarkMode ? "#e5e7eb" : "#374151";
          }
        },
      });
      navigate(`${SERVICE_DESK_BASE}/tickets`);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { meta?: { message?: string } } } })?.response?.data?.meta?.message;
      Swal.fire({
        icon: "error",
        title: "Error al eliminar",
        text: typeof msg === "string" ? msg : "No se pudo eliminar el ticket",
        confirmButtonColor: themeBrand.primary,
        background: isDarkMode ? "#1f2937" : "#fff",
        color: isDarkMode ? "#fff" : "#1f2937",
        didOpen: () => {
          const title = document.querySelector<HTMLElement>(".swal2-title");
          const content = document.querySelector<HTMLElement>(".swal2-html-container");
          if (title) {
            title.style.color = isDarkMode ? "#ffffff" : "#1f2937";
          }
          if (content) {
            content.style.color = isDarkMode ? "#e5e7eb" : "#374151";
          }
        },
      });
    }
  };

  // Devuelve el ticket a la cola del agente IA. Útil cuando un intento falló o
  // cuando quieres que lo reintente tras aclarar la descripción del problema.
  const onRetryAi = async () => {
    const { isConfirmed } = await Swal.fire({
      icon: "question",
      title: t("ticketDetail.aiAgent.retryTitle"),
      text: t("ticketDetail.aiAgent.retryText"),
      showCancelButton: true,
      confirmButtonText: t("ticketDetail.aiAgent.retryConfirm"),
      confirmButtonColor: themeBrand.primary,
      background: isDarkMode ? "#1f2937" : "#fff",
      color: isDarkMode ? "#fff" : "#1f2937",
    });
    if (!isConfirmed) return;
    try {
      // La respuesta es la proyección del runner (parcial): no pisar el ticket
      // con ella, recargar el detalle completo.
      await releaseAiAgent(id, true);
      await load();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { meta?: { message?: string } } } })?.response?.data?.meta?.message;
      Swal.fire({
        icon: "error",
        title: t("ticketDetail.aiAgent.retryError"),
        text: typeof msg === "string" ? msg : "",
        background: isDarkMode ? "#1f2937" : "#fff",
        color: isDarkMode ? "#fff" : "#1f2937",
      });
    }
  };

  // Respuesta con IA de cara al cliente: la marca «sin IA» de este ticket y
  // «Reactivar la conversación» (el backend exige un aprobador para esto).
  const showAiReplyError = (err: unknown, title: string) => {
    const msg = (err as { response?: { data?: { meta?: { message?: string } } } })?.response?.data?.meta?.message;
    Swal.fire({
      icon: "error",
      title,
      text: typeof msg === "string" ? msg : "",
      background: isDarkMode ? "#1f2937" : "#fff",
      color: isDarkMode ? "#fff" : "#1f2937",
    });
  };

  const onToggleAiReplyDisabled = async (disabled: boolean) => {
    try {
      await updateTicket(id, { aiFirstReplyDisabled: disabled });
      await load();
    } catch (err: unknown) {
      showAiReplyError(err, t("ticketDetail.updateError"));
    }
  };

  const onResumeAiConversation = async () => {
    const { isConfirmed } = await Swal.fire({
      icon: "question",
      title: t("ticketDetail.aiReply.conversation.resumeTitle"),
      text: t("ticketDetail.aiReply.conversation.resumeText"),
      showCancelButton: true,
      confirmButtonText: t("ticketDetail.aiReply.conversation.resumeConfirm"),
      cancelButtonText: t("ticketDetail.aiAgent.cancel"),
      confirmButtonColor: themeBrand.primary,
      background: isDarkMode ? "#1f2937" : "#fff",
      color: isDarkMode ? "#fff" : "#1f2937",
    });
    if (!isConfirmed) return;
    try {
      await updateTicket(id, { aiConversationResume: true });
      await load();
    } catch (err: unknown) {
      showAiReplyError(err, t("ticketDetail.aiReply.conversation.resumeError"));
    }
  };

  // Compuerta de confianza: sólo tras esta aprobación (auditada) el agente IA
  // puede tomar el ticket. Los tickets del portal público siempre la requieren.
  // `running`: el agente tiene el ticket tomado; retirar la aprobación detiene
  // la corrida en su próximo punto de control (antes del push o del PR).
  const onToggleAiApproval = async (approve: boolean, running = false) => {
    const revokeText = running ? "ticketDetail.aiAgent.revokeTextRunning" : "ticketDetail.aiAgent.revokeText";
    const { isConfirmed } = await Swal.fire({
      icon: approve ? "question" : "warning",
      title: t(approve ? "ticketDetail.aiAgent.approveTitle" : running ? "ticketDetail.aiAgent.revokeTitleRunning" : "ticketDetail.aiAgent.revokeTitle"),
      text: t(approve ? "ticketDetail.aiAgent.approveText" : revokeText),
      showCancelButton: true,
      confirmButtonText: t(approve ? "ticketDetail.aiAgent.approveConfirm" : running ? "ticketDetail.aiAgent.revokeConfirmRunning" : "ticketDetail.aiAgent.revokeConfirm"),
      cancelButtonText: t("ticketDetail.aiAgent.cancel"),
      confirmButtonColor: approve ? themeBrand.primary : "#dc2626",
      background: isDarkMode ? "#1f2937" : "#fff",
      color: isDarkMode ? "#fff" : "#1f2937",
    });
    if (!isConfirmed) return;
    try {
      await (approve ? approveAiAgent(id) : revokeAiAgentApproval(id));
      await load();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { meta?: { message?: string }; message?: string } } })?.response?.data;
      Swal.fire({
        icon: "error",
        title: t("ticketDetail.aiAgent.approvalError"),
        text: typeof msg?.message === "string" ? msg.message : typeof msg?.meta?.message === "string" ? msg.meta.message : "",
        background: isDarkMode ? "#1f2937" : "#fff",
        color: isDarkMode ? "#fff" : "#1f2937",
      });
    }
  };

  // Puente a ingeniería (#510): al aprobar, el backend encola el trabajo del
  // runner y crea (o reutiliza) el issue de GitHub. Si quedó pendiente (sin
  // GH_TOKEN, repo sin resolver) o falló, esto lo reintenta. El repo sólo se
  // pregunta cuando el backend no pudo resolverlo.
  const onRetryBridge = async () => {
    const swalTheme = { background: isDarkMode ? "#1f2937" : "#fff", color: isDarkMode ? "#fff" : "#1f2937" };
    let repo: string | undefined;
    if (engineeringBridgeNeedsRepo(ticket?.engineeringBridge)) {
      // Sólo la lista blanca de la mesa: el backend rechaza cualquier otro repo.
      const repos = bridgeRepoChoices(settings?.githubRepos);
      if (!repos.length) {
        Swal.fire({ icon: "info", title: t("ticketDetail.bridge.pickRepoTitle"), text: t("ticketDetail.bridge.noRepos"), ...swalTheme });
        return;
      }
      const picked = await Swal.fire({
        title: t("ticketDetail.bridge.pickRepoTitle"),
        input: "select",
        inputOptions: Object.fromEntries(repos.map((r) => [r, r])),
        inputLabel: t("ticketDetail.bridge.pickRepoLabel"),
        inputValue: repos[0],
        showCancelButton: true,
        confirmButtonText: t("ticketDetail.bridge.pickRepoConfirm"),
        cancelButtonText: t("ticketDetail.aiAgent.cancel"),
        confirmButtonColor: themeBrand.primary,
        ...swalTheme,
      });
      if (!picked.isConfirmed) return;
      repo = String(picked.value ?? "") || undefined;
    }
    setSaving(true);
    try {
      const out = await retryServiceDeskBridge(id, { repo });
      // Pintar ya el estado devuelto; la recarga trae además el issue vinculado.
      if (out?.engineeringBridge !== undefined) {
        setTicket((prev) => (prev ? { ...prev, engineeringBridge: out.engineeringBridge } : prev));
      }
      await load();
    } catch (err: unknown) {
      Swal.fire({ icon: "error", title: t("ticketDetail.bridge.retryError"), text: apiErrorMessage(err), ...swalTheme });
    } finally {
      setSaving(false);
    }
  };

  // Etapa del ciclo de desarrollo (#559). El cliente ve el resultado, así que
  // se pide confirmación con una nota opcional que sólo queda en la vista
  // interna.
  const onChangeStage = async (stage: DevStageId) => {
    if (!ticket || stage === (ticket.devStage?.current || "received")) return;
    const swalTheme = { background: isDarkMode ? "#1f2937" : "#fff", color: isDarkMode ? "#fff" : "#1f2937" };
    const { isConfirmed, value: note } = await Swal.fire({
      icon: "question",
      title: t("ticketDetail.stage.confirmTitle"),
      text: t("ticketDetail.stage.confirmText"),
      input: "text",
      inputPlaceholder: t("ticketDetail.stage.notePlaceholder"),
      inputAttributes: { maxlength: "500" },
      showCancelButton: true,
      confirmButtonText: t("ticketDetail.stage.confirm"),
      cancelButtonText: t("ticketDetail.stage.cancel"),
      confirmButtonColor: themeBrand.primary,
      ...swalTheme,
    });
    if (!isConfirmed) return;
    setSaving(true);
    try {
      const trimmed = typeof note === "string" ? note.trim() : "";
      const out = await setTicketStage(id, trimmed ? { stage, note: trimmed } : { stage });
      if (out?.devStage) setTicket((prev) => (prev ? { ...prev, devStage: out.devStage } : prev));
      await load();
    } catch (err: unknown) {
      Swal.fire({ icon: "error", title: t("ticketDetail.stage.error"), text: apiErrorMessage(err), ...swalTheme });
    } finally {
      setSaving(false);
    }
  };

  const card = isDarkMode ? "bg-dark-surface border-dark-border" : "bg-white border-gray-200";
  const text = isDarkMode ? "text-white" : "text-gray-900";
  const muted = isDarkMode ? "text-gray-400" : "text-gray-500";
  const selCls = `w-full rounded-lg border px-3 py-1.5 text-sm ${isDarkMode ? "!bg-gray-800 !border-gray-600 !text-white" : "!bg-white !border-gray-300"}`;

  if (!authChecked) return <div className="p-8 text-center text-gray-400">Verificando autenticación...</div>;
  if (loading) return <div className="p-8 text-center text-gray-400">{t("ticketDetail.loading")}</div>;
  if (!ticket || !settings) return <div className="p-8 text-center text-gray-400">{t("ticketDetail.notFound")}</div>;

  const toggleLabel = (labelId: string) => {
    const set = new Set(ticket.labels || []);
    if (set.has(labelId)) set.delete(labelId); else set.add(labelId);
    patch({ labels: Array.from(set) });
  };

  // Etiquetas que el ticket arrastra pero que ya NO están en Ajustes (borrar una
  // etiqueta no hace cascada). El backend las conserva al editar, así que aquí se
  // muestran para que el staff pueda verlas y quitarlas; no se pueden volver a
  // añadir una vez quitadas.
  const legacyLabels = legacyTicketLabels(settings.labels, ticket.labels);

  const internalCount = comments.filter((c) => c.internal).length;

  // Cierre por solicitante (#729): quién puede pasar ESTE ticket a «Cerrado».
  // Se calcula con la misma regla que el backend para deshabilitar la opción y
  // explicar por qué; si aun así se intenta, el 403 trae su mensaje y `patch`
  // lo muestra.
  const closePermission = canCloseTicket(ticket, { id: user?.id, email: user?.email }, Date.now());
  const isClosed = ticket.status === CLOSED_STATUS;
  const closureHint = isClosed
    ? null
    : closePermission.reason === "locked" && closePermission.unlockAt == null
      ? t("closure.hint.lockedNoDate", { days: ticket.closure?.lockDays ?? 7 })
      : t(`closure.hint.${closePermission.reason}`, {
          name: ticket.requester?.name || ticket.requester?.email || t("closure.hint.requesterFallback"),
          date: formatClosureDate(closePermission.unlockAt),
          days: ticket.closure?.lockDays ?? 7,
        });
  const rework = ticket.rework && ticket.rework.count > 0 ? ticket.rework : null;

  return (
    <div className="p-6">
      <button onClick={() => navigate(`${SERVICE_DESK_BASE}/tickets`)} className={`mb-4 text-sm ${muted} hover:underline`}>← {t("ticketsList.title")}</button>

      <div className="grid gap-5 lg:grid-cols-3">
        {/* Main */}
        <div className="space-y-5 lg:col-span-2">
          <div className={`rounded-xl border p-5 ${card}`}>
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <span className="font-mono text-xs text-gray-400">{ticket.reference}</span>
              <PriorityBadge priority={ticket.priority} />
              <SlaBadge state={ticket.slaStatus?.overall} />
              <AiAgentBadge state={ticket.aiAgent?.state} />
              <ReworkChip count={ticket.rework?.count} />
              {ticket.slaStatus?.paused && <span className="text-xs text-amber-600">SLA en pausa</span>}
              {ticket.escalated && <span className="rounded bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-600">Escalado</span>}
            </div>
            <h1 className={`text-xl font-bold ${text}`}>{ticket.title}</h1>
            <p className={`mt-1 text-xs ${muted}`}>
              {ticket.brandLabel} · {ticket.requester?.name || "Anónimo"} ({ticket.requester?.email || "sin correo"}) · {formatDate(ticket.createdAt)}
            </p>
            {isReseller && (
              <div className="mt-2 grid gap-3 sm:grid-cols-2">
                <div>
                  <label className={`block text-xs font-medium ${muted} mb-1`}>Compañía *</label>
                  <select
                    className={selCls}
                    value={ticket.companyId || ""}
                    onChange={(e) => patch({ companyId: e.target.value || null })}
                    disabled={saving}
                  >
                    {/* Los tickets nuevos siempre traen compañía; esta opción sólo
                        aparece para el histórico anterior a la regla. */}
                    {!ticket.companyId && <option value="">Sin compañía (ticket antiguo)</option>}
                    {allCompanies.map((c) => (
                      <option key={c.id} value={c.id}>{c.name || c.business_name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={`block text-xs font-medium ${muted} mb-1`}>Usuario afectado</label>
                  <select
                    className={selCls}
                    value={ticket.affectedUser?.id || ""}
                    onChange={(e) => patch({ affectedUserId: e.target.value || null })}
                    disabled={saving || !ticket.companyId || companyUsersLoading}
                  >
                    {/* Obligatorio una vez que el ticket tiene compañía. El valor
                        actual siempre tiene su opción (deshabilitada si no está en
                        la lista), para no mostrar a otra persona como seleccionada. */}
                    {buildAffectedUserOptions(ticket.affectedUser, companyUsers, companyUsersLoading).map((o) => (
                      <option key={o.value || "__empty"} value={o.value} disabled={o.disabled}>{o.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
            {!isReseller && ticket.companyId && (
              <p className={`mt-2 text-xs ${muted}`}>
                Compañía:{" "}
                {allCompanies.find(c => c.id === ticket.companyId)?.name ||
                  resolvedCompanyNames[ticket.companyId] ||
                  ticket.companyId}
              </p>
            )}
            {ticket.affectedUser && (
              <p className={`mt-2 text-xs ${muted}`}>
                Usuario afectado: <strong>{ticket.affectedUser.name}</strong>
                {ticket.affectedUser.email ? ` (${ticket.affectedUser.email})` : ""} · Rol:{" "}
                <strong>{ticket.affectedUser.roleName || "sin rol asignado"}</strong>
              </p>
            )}
            {!ticket.companyId && (
              <p className="mt-2 rounded bg-amber-100 px-2 py-1 text-xs text-amber-800 dark:bg-amber-500/20 dark:text-amber-200">
                Este ticket se creó sin compañía. Asígnale una para poder registrar al usuario afectado.
              </p>
            )}
            {ticket.description && <p className={`mt-4 whitespace-pre-wrap text-sm ${text}`}>{ticket.description}</p>}
          </div>

          {/* Public thread — only customer-facing comments; replies notify the client. */}
          <div className={`rounded-xl border p-5 ${card}`}>
            <h2 className={`mb-3 text-sm font-semibold ${text}`}>{t("ticketDetail.comments.title")}</h2>
            <CommentThread
              comments={comments}
              onReply={onReply}
              replyLabel={t("ticketDetail.comments.replyPlaceholder")}
              emptyLabel={t("ticketDetail.comments.noComments")}
            />
          </div>

          {/* Internal team notes — invisible to the customer, never emailed (#34). */}
          <div className="rounded-xl border border-amber-300 bg-amber-50/60 p-5 dark:bg-amber-500/10">
            <div className="mb-1 flex items-center gap-2">
              <h2 className="text-sm font-semibold text-amber-800 dark:text-amber-300">{t("ticketDetail.comments.internal")}</h2>
              {internalCount > 0 && (
                <span className="rounded-full bg-amber-200 px-2 py-0.5 text-xs font-semibold text-amber-800">{internalCount}</span>
              )}
            </div>
            <p className="mb-3 text-xs text-amber-700 dark:text-amber-300/80">
              {t("ticketDetail.comments.internalToggle")}
            </p>
            <CommentThread
              comments={comments}
              onReply={onReply}
              internalOnly
              replyLabel={t("ticketDetail.comments.replyPlaceholder")}
            />
          </div>

          <div className={`rounded-xl border p-5 ${card}`}>
            <h2 className={`mb-3 text-sm font-semibold ${text}`}>{t("ticketDetail.auditTrail")}</h2>
            {audit.length === 0 ? (
              <p className="text-sm text-gray-400">{t("ticketDetail.noAuditEvents")}</p>
            ) : (
              <ul className="space-y-2">
                {audit.map((a) => {
                  const actionKey = `audit.actions.${a.action}`;
                  const actionLabel = t(actionKey) !== actionKey ? t(actionKey) : a.action;
                  const formatValue = (field: string | undefined, value: unknown): string => {
                    if (!value || value === "—") return "—";
                    const strVal = String(value);
                    if (field === "priority") {
                      const priorityMap: Record<string, string> = {
                        low: t("ticketDetail.management.priorities.low"),
                        medium: t("ticketDetail.management.priorities.medium"),
                        high: t("ticketDetail.management.priorities.high"),
                        urgent: t("ticketDetail.management.priorities.urgent"),
                      };
                      return priorityMap[strVal] || strVal;
                    }
                    if (field === "assignedAgentId") {
                      const agent = users.find(u => u.id === strVal);
                      return agent?.name || strVal;
                    }
                    return strVal;
                  };
                  return (
                    <li key={a.id} className={`flex items-start justify-between text-xs ${muted}`}>
                      <span>
                        <strong>{a.actorName || t("audit.system")}</strong> — {actionLabel}
                        {a.field && a.to !== undefined ? `: ${formatValue(a.field, a.from)} → ${formatValue(a.field, a.to)}` : ""}
                      </span>
                      <span className="ml-3 whitespace-nowrap">{formatDate(a.createdAt)}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        {/* Management sidebar */}
        <div className="space-y-4">
          {/* Ciclo de desarrollo (#559) — VISTA INTERNA: línea de tiempo con
              enlaces a issue / PR / QA y la bitácora de quién movió qué. */}
          <DevStagePanel
            devStage={ticket.devStage}
            settings={settings}
            isDarkMode={isDarkMode}
            saving={saving}
            onChangeStage={onChangeStage}
          />
          <div className={`rounded-xl border p-5 ${card}`}>
            <h2 className={`mb-3 text-sm font-semibold ${text}`}>{t("ticketDetail.management.title")}</h2>
            <FieldRow label={t("ticketDetail.management.status")} muted={muted}>
              <select className={selCls} value={ticket.status} onChange={(e) => patch({ status: e.target.value })} disabled={saving}>
                {(settings.statuses || [])
                  .filter((s) => allowedNextStatuses(settings, ticket.status).includes(s.id))
                  .map((s) => (
                    <option key={s.id} value={s.id} disabled={s.id === CLOSED_STATUS && !isClosed && !closePermission.allowed}>
                      {s.label}
                    </option>
                  ))}
              </select>
              {closureHint && (
                <p className={`mt-1 text-xs ${closePermission.allowed ? muted : "text-amber-600"}`}>{closureHint}</p>
              )}
            </FieldRow>
            <FieldRow label={t("ticketDetail.management.priority")} muted={muted}>
              <select className={selCls} value={ticket.priority} onChange={(e) => patch({ priority: e.target.value as TicketPriority })} disabled={saving}>
                <option value="low">{t("ticketDetail.management.priorities.low")}</option>
                <option value="medium">{t("ticketDetail.management.priorities.medium")}</option>
                <option value="high">{t("ticketDetail.management.priorities.high")}</option>
                <option value="urgent">{t("ticketDetail.management.priorities.urgent")}</option>
              </select>
            </FieldRow>
            <FieldRow label={t("ticketDetail.management.brand")} muted={muted}>
              <select className={selCls} value={ticket.brand} onChange={(e) => patch({ brand: e.target.value })} disabled={saving}>
                {(settings.brands || []).map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
              </select>
            </FieldRow>
            <FieldRow label={t("ticketDetail.management.category")} muted={muted}>
              <select className={selCls} value={ticket.category} onChange={(e) => patch({ category: e.target.value })} disabled={saving}>
                {(settings.categories || []).map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </FieldRow>
            <FieldRow label="Módulo afectado" muted={muted}>
              <select className={selCls} value={ticket.module || ""} onChange={(e) => patch({ module: e.target.value || "" })} disabled={saving}>
                <option value="">Sin módulo</option>
                {MODULE_OPTIONS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
              </select>
            </FieldRow>
            {ticket.module && (MODULE_OPTIONS.find(m => m.id === ticket.module)?.sections?.length ?? 0) > 0 && (
              <FieldRow label="Sección" muted={muted}>
                <select className={selCls} value={ticket.moduleSection || ""} onChange={(e) => patch({ moduleSection: e.target.value || "" })} disabled={saving}>
                  <option value="">Sin sección</option>
                  {MODULE_OPTIONS.find(m => m.id === ticket.module)?.sections.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
              </FieldRow>
            )}
            <FieldRow label={t("ticketDetail.management.assignedAgent")} muted={muted}>
              <AgentSelector
                value={ticket.assignedAgentId}
                onChange={(agentId) => patch({ assignedAgentId: agentId })}
                isDarkMode={isDarkMode}
                users={users}
                placeholder={t("ticketDetail.management.notAssigned")}
                isDisabled={saving}
              />
            </FieldRow>
          </div>

          {/* Cierre y tiempos (#729): lo que NO es tiempo del equipo — la espera
              al cliente y lo que tarda el solicitante en confirmar — y cuántas
              veces volvió de «Resuelto». */}
          <div className={`rounded-xl border p-5 ${card}`}>
            <h2 className={`mb-3 text-sm font-semibold ${text}`}>{t("closure.panel.title")}</h2>
            <dl className={`space-y-1.5 text-xs ${muted}`}>
              <div className="flex justify-between gap-2">
                <dt>{t("closure.panel.waitingCustomer")}</dt>
                <dd className={text}>{formatDurationMs(ticket.slaStatus?.waitingCustomerMs)}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt>{t("closure.panel.confirmation")}</dt>
                <dd className={`text-right ${text}`}>
                  {formatDurationMs(ticket.slaStatus?.closureMs)}
                  {ticket.closure?.pending && ticket.slaStatus?.closureMs != null ? ` (${t("closure.panel.running")})` : ""}
                </dd>
              </div>
              {ticket.closure?.pending && (
                <div className="flex justify-between gap-2">
                  <dt>{t("closure.panel.resolvedAt")}</dt>
                  <dd className={text}>{formatDate(ticket.closure.resolvedAt)}</dd>
                </div>
              )}
              {ticket.closedBy && (
                <div className="flex justify-between gap-2">
                  <dt>{t("closure.panel.closedBy")}</dt>
                  <dd className={`text-right ${text}`}>
                    {ticket.closedBy.name || "—"} ({t(`closure.panel.roles.${ticket.closedBy.role}`)})
                  </dd>
                </div>
              )}
              <div className="flex justify-between gap-2">
                <dt>{t("closure.panel.reworkCount")}</dt>
                <dd className={text}>{ticket.rework?.count ?? 0}</dd>
              </div>
            </dl>
            {rework && (rework.lastReason || rework.lastByName) && (
              <div className="mt-3 rounded-lg bg-orange-50 p-3 text-xs text-orange-800 dark:bg-orange-500/10 dark:text-orange-200">
                <p className="font-semibold">
                  {t("closure.panel.lastRework")}
                  {rework.lastByName ? ` · ${rework.lastByName}` : ""}
                  {rework.lastByRole ? ` (${t(`closure.panel.reworkRoles.${rework.lastByRole}`)})` : ""}
                  {rework.lastAt ? ` · ${formatDate(rework.lastAt)}` : ""}
                </p>
                {rework.lastReason && <p className="mt-1 whitespace-pre-wrap">{rework.lastReason}</p>}
              </div>
            )}
          </div>

          <div className={`rounded-xl border p-5 ${card}`}>
            <h2 className={`mb-3 text-sm font-semibold ${text}`}>Etiquetas</h2>
            <div className="flex flex-wrap gap-2">
              {(settings.labels || []).map((l) => {
                const active = (ticket.labels || []).includes(l.id);
                return (
                  <button
                    key={l.id}
                    onClick={() => toggleLabel(l.id)}
                    disabled={saving}
                    className="rounded-full px-2.5 py-1 text-xs font-medium"
                    style={{ color: active ? "#fff" : l.color, backgroundColor: active ? l.color : `${l.color}1a` }}
                  >
                    {l.label}
                  </button>
                );
              })}
              {legacyLabels.map((id) => (
                <button
                  key={`legacy-${id}`}
                  onClick={() => toggleLabel(id)}
                  disabled={saving}
                  title="Etiqueta que ya no está configurada en Ajustes. Puedes quitarla, pero no volver a añadirla."
                  className="rounded-full border border-dashed px-2.5 py-1 text-xs font-medium"
                  style={{ color: "#fff", backgroundColor: "#9CA3AF" }}
                >
                  {id}
                </button>
              ))}
            </div>
            {legacyLabels.length > 0 && (
              <p className={`mt-2 text-xs ${muted}`}>
                Las etiquetas en gris ya no están configuradas en Ajustes: se conservan y puedes quitarlas.
              </p>
            )}
          </div>

          <div className={`rounded-xl border p-5 ${card}`}>
            <h2 className={`mb-3 text-sm font-semibold ${text}`}>{t("ticketDetail.management.escalation.title")}</h2>
            <label className={`flex items-center gap-2 text-sm ${text}`}>
              <input type="checkbox" checked={Boolean(ticket.escalated)} onChange={(e) => patch({ escalated: e.target.checked })} disabled={saving} />
              {t("ticketDetail.management.escalation.label")}
            </label>
            {ticket.escalated && (
              <FieldRow label={t("ticketDetail.management.escalation.assignedDev")} muted={muted}>
                <AgentSelector
                  value={ticket.assignedDevId}
                  onChange={(agentId) => patch({ assignedDevId: agentId })}
                  isDarkMode={isDarkMode}
                  users={users}
                  placeholder={t("ticketDetail.management.notAssigned")}
                  isDisabled={saving}
                />
              </FieldRow>
            )}
          </div>

          {/* Agente IA — lo escribe el runner local vía /serviceDesk/ai/*. Aquí
              el staff sólo aprueba, retira la aprobación o reintenta. */}
          {(ticket.aiAgent || !["resolved", "closed"].includes(ticket.status)) && (() => {
            const ai = ticket.aiAgent ?? { state: "idle" as const };
            const approved = ai.approved === true && !!ai.approvedBy;
            const approvedAt = tsToDate(ai.approvedAt);
            return (
            <div className={`rounded-xl border p-5 ${card}`}>
              <div className="mb-3 flex items-center justify-between">
                <h2 className={`text-sm font-semibold ${text}`}>{t("ticketDetail.aiAgent.title")}</h2>
                <AiAgentBadge state={ai.state} />
              </div>

              <div className={`mb-3 rounded-lg p-3 text-xs ${approved ? "bg-green-50 text-green-700 dark:bg-green-500/10" : "bg-amber-50 text-amber-700 dark:bg-amber-500/10"}`}>
                {approved ? (
                  <>
                    <span>
                      {t("ticketDetail.aiAgent.approvedBy", { name: ai.approvedByName || ai.approvedBy })}
                      {approvedAt ? ` · ${formatDate(ai.approvedAt)}` : ""}
                    </span>
                    <span className="mt-1 block">{t("ticketDetail.aiAgent.approvalCoversContent")}</span>
                  </>
                ) : (
                  <span>{t("ticketDetail.aiAgent.pendingApproval")}</span>
                )}
                {ticket.source !== "authenticated" && (
                  <span className="mt-1 block">{t("ticketDetail.aiAgent.publicAlwaysApproval")}</span>
                )}
              </div>

              <EngineeringBridgeStatus
                bridge={ticket.engineeringBridge}
                isDarkMode={isDarkMode}
                busy={saving}
                onRetry={onRetryBridge}
              />

              {ai.planSummary && (
                <p className={`mb-3 whitespace-pre-wrap text-xs ${text}`}>{ai.planSummary}</p>
              )}

              <dl className={`space-y-1 text-xs ${muted}`}>
                {ai.repo && (
                  <div className="flex justify-between gap-2">
                    <dt>{t("ticketDetail.aiAgent.repo")}</dt>
                    <dd className={`font-mono ${text}`}>{ai.repo}</dd>
                  </div>
                )}
                {ai.branch && (
                  <div className="flex justify-between gap-2">
                    <dt>{t("ticketDetail.aiAgent.branch")}</dt>
                    <dd className={`truncate font-mono ${text}`} title={ai.branch}>{ai.branch}</dd>
                  </div>
                )}
                <div className="flex justify-between gap-2">
                  <dt>{t("ticketDetail.aiAgent.attempts")}</dt>
                  <dd className={text}>{ai.attempts ?? 0}</dd>
                </div>
                {ai.workedAt && (
                  <div className="flex justify-between gap-2">
                    <dt>{t("ticketDetail.aiAgent.lastRun")}</dt>
                    <dd className={text}>{formatDate(ai.workedAt)}</dd>
                  </div>
                )}
              </dl>

              {ai.lastError && (
                <p className="mt-3 rounded bg-red-50 p-2 text-xs text-red-600 dark:bg-red-500/10">
                  {ai.lastError}
                </p>
              )}

              <div className="mt-3 flex flex-col gap-2">
                {ai.prUrl && (
                  <a href={ai.prUrl} target="_blank" rel="noreferrer" className="text-sm font-medium text-blue-500 underline">
                    {t("ticketDetail.aiAgent.viewPr")}
                  </a>
                )}
                {ai.issueUrl && !ticket.githubIssue?.htmlUrl && (
                  <a href={ai.issueUrl} target="_blank" rel="noreferrer" className="text-sm font-medium text-blue-500 underline">
                    {t("ticketDetail.aiAgent.viewIssue")}
                  </a>
                )}
                {/* Aprobar / retirar la aprobación (compuerta de confianza) y
                    Reintentar. Retirar sigue disponible con el agente
                    trabajando: es justo cuando detiene la corrida. */}
                <AiAgentActions
                  ticket={ticket}
                  isDarkMode={isDarkMode}
                  primaryColor={themeBrand.primary}
                  onApprove={() => onToggleAiApproval(true)}
                  onRevoke={(running) => onToggleAiApproval(false, running)}
                  onRetry={onRetryAi}
                />
              </div>

              <p className={`mt-3 text-[11px] ${muted}`}>{t("ticketDetail.aiAgent.footnote")}</p>
            </div>
            );
          })()}

          {/* Respuesta con IA de cara al cliente (primera respuesta +
              conversación). Distinta del Agente IA de código de arriba. */}
          <AiReplyPanel
            ticket={ticket}
            isDarkMode={isDarkMode}
            primaryColor={themeBrand.primary}
            saving={saving}
            onToggleDisabled={onToggleAiReplyDisabled}
            onResume={onResumeAiConversation}
          />

          <div className={`rounded-xl border p-5 ${card}`}>
            <h2 className={`mb-3 text-sm font-semibold ${text}`}>{t("ticketDetail.github.title")}</h2>
            {ticket.githubIssue?.htmlUrl ? (
              <div className="space-y-3">
                <a href={ticket.githubIssue.htmlUrl} target="_blank" rel="noreferrer" className={`block text-sm font-medium ${isDarkMode ? "text-blue-400" : "text-blue-600"} underline`}>
                  #{ticket.githubIssue.number} {ticket.githubIssue.repo && `· ${ticket.githubIssue.repo}`}
                </a>
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-semibold rounded-full px-2.5 py-1 ${
                    ticket.githubIssue.state === 'closed'
                      ? isDarkMode ? 'bg-purple-900/40 text-purple-300' : 'bg-purple-100 text-purple-700'
                      : isDarkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-700'
                  }`}>
                    {ticket.githubIssue.state === 'closed' ? '✓ Cerrada' : '◯ Abierta'}
                  </span>
                </div>
                <button onClick={onLinkExistingGithub} className={`text-xs ${muted} hover:underline`}>
                  {t("ticketDetail.github.linkExisting")}
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <button onClick={onLinkGithub} className="rounded-lg px-4 py-2 text-sm font-semibold text-white hover:opacity-90" style={{ backgroundColor: themeBrand.primary }}>
                  {t("ticketDetail.github.createAndLink")}
                </button>
                <button onClick={onLinkExistingGithub} className={`rounded-lg border px-4 py-2 text-sm font-semibold ${isDarkMode ? "border-gray-600 text-gray-200 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-gray-50"}`}>
                  {t("ticketDetail.github.linkExisting")}
                </button>
              </div>
            )}
          </div>

          {isReseller && (
            <div className={`rounded-xl border p-5 ${card}`}>
              <h2 className={`mb-3 text-sm font-semibold ${text}`}>Acciones</h2>
              <button
                onClick={onDeleteTicket}
                className="w-full rounded-lg border border-red-300 bg-red-50 px-4 py-2 text-sm font-semibold text-red-700 transition-colors hover:bg-red-100 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300 dark:hover:bg-red-900/50"
              >
                Eliminar ticket
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modal selector de repositorio GitHub */}
      {showGithubModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className={`rounded-lg p-6 shadow-lg ${isDarkMode ? "bg-dark-surface" : "bg-white"} w-96`} style={{ maxHeight: '85vh', overflow: 'visible' }}>
            <h2 className={`mb-4 text-lg font-bold ${text}`}>Selecciona un repositorio</h2>
            <div style={{ position: 'relative', zIndex: 50 }}>
              <Select
                options={repositories.map(r => ({
                  value: r.fullName,
                  label: `${r.fullName}${r.isPrivate ? ' (privado)' : ''}`,
                }))}
                value={selectedRepo}
                onChange={setSelectedRepo}
                placeholder="Busca un repositorio..."
                isClearable
                isSearchable
                maxMenuHeight={300}
                styles={{
                  control: (base: any) => ({
                    ...base,
                    backgroundColor: isDarkMode ? '#374151' : '#fff',
                    borderColor: isDarkMode ? '#4b5563' : '#ddd',
                    color: isDarkMode ? '#fff' : '#000',
                    boxShadow: 'none',
                    '&:hover': {
                      borderColor: isDarkMode ? '#6b7280' : '#999',
                    },
                  }),
                  menuPortal: (base: any) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                  menu: (base: any) => ({
                    ...base,
                    backgroundColor: isDarkMode ? '#374151' : '#fff',
                    border: `1px solid ${isDarkMode ? '#4b5563' : '#ddd'}`,
                    zIndex: 9999,
                  }),
                  option: (base: any, state: any) => ({
                    ...base,
                    backgroundColor: state.isSelected
                      ? themeBrand.primary
                      : state.isFocused
                        ? isDarkMode ? '#4b5563' : '#f3f4f6'
                        : isDarkMode ? '#374151' : '#fff',
                    color: state.isSelected ? '#fff' : isDarkMode ? '#fff' : '#000',
                    cursor: 'pointer',
                    padding: '8px 12px',
                  }),
                  singleValue: (base: any) => ({
                    ...base,
                    color: isDarkMode ? '#fff' : '#000',
                  }),
                  input: (base: any) => ({
                    ...base,
                    color: isDarkMode ? '#fff' : '#000',
                  }),
                  placeholder: (base: any) => ({
                    ...base,
                    color: isDarkMode ? '#9ca3af' : '#999',
                  }),
                }}
                menuPortalTarget={document.body}
              />
            </div>
            <div className="mt-6 flex gap-3">
              <button
                onClick={handleGithubConfirm}
                disabled={!selectedRepo}
                className="flex-1 rounded-lg px-4 py-2 font-semibold text-white hover:opacity-90 disabled:opacity-50"
                style={{ backgroundColor: themeBrand.primary }}
              >
                Crear issue
              </button>
              <button
                onClick={() => setShowGithubModal(false)}
                className={`flex-1 rounded-lg border px-4 py-2 font-semibold ${isDarkMode ? "border-gray-600 text-gray-200 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-gray-50"}`}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FieldRow({ label, muted, children }: { label: string; muted: string; children: React.ReactNode }) {
  return (
    <div className="mb-3">
      <span className={`mb-1 block text-xs ${muted}`}>{label}</span>
      {children}
    </div>
  );
}
